import React, { useState, useRef, useEffect } from "react";
import { Notifications as NotificationsSkyla } from "../icons";
import { notificationService } from "../../services/notificationService";

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("unread");
  const [notifications, setNotifications] = useState({ unread: [], read: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const menuRef = useRef(null);
  const iconRef = useRef(null);
  const uid = localStorage.getItem("uid");

  const fetchUnreadCount = async () => {
    if (!uid) return;
    const data = await notificationService.getUnreadCount(uid);
    if (data?.success) setUnreadCount(data.unreadCount || 0);
  };

  const fetchNotifications = async () => {
    if (!uid) return;
    setIsLoading(true);
    try {
      const data = await notificationService.getHistory(uid);
      if (data?.success) {
        setNotifications({
          unread: data.notifications?.unread || [],
          read: data.notifications?.read || [],
        });
        await fetchUnreadCount();
      }
    } finally { setIsLoading(false); }
  };

  const markAsRead = async (id) => {
    const res = await notificationService.markAsRead(uid, [id]);
    if (res?.success) {
      setNotifications((prev) => {
        const moved = prev.unread.find((n) => n.id === id);
        return {
          unread: prev.unread.filter((n) => n.id !== id),
          read: moved ? [...prev.read, { ...moved, is_read: true }] : prev.read,
        };
      });
      await fetchUnreadCount();
    }
  };

  const markAllAsRead = async () => {
    const ids = notifications.unread.map((n) => n.id);
    if (ids.length === 0) return;
    const res = await notificationService.markAsRead(uid, ids);
    if (res?.success) {
      setNotifications((prev) => ({
        unread: [],
        read: [...prev.read, ...prev.unread.map((n) => ({ ...n, is_read: true }))],
      }));
      await fetchUnreadCount();
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && menuRef.current && !menuRef.current.contains(e.target) && iconRef.current && !iconRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => { if (isOpen) fetchNotifications(); }, [isOpen]);
  useEffect(() => { fetchUnreadCount(); }, []);

  const formatTime = (ts) => {
    const diff = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diff / 60000), h = Math.floor(m / 60), d = Math.floor(h / 24);
    if (d > 0) return `${d}d ago`;
    if (h > 0) return `${h}h ago`;
    if (m > 0) return `${m}m ago`;
    return "Just now";
  };

  const list = activeTab === "unread" ? notifications.unread : notifications.read;

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <div ref={iconRef} onClick={() => setIsOpen(!isOpen)} style={{ cursor: "pointer", position: "relative" }}>
        <NotificationsSkyla size="20px" />
        <span style={{
          position: "absolute", top: -8, right: -8, minWidth: 20, height: 20,
          background: "#FFD700", color: "#FF0000", borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, fontWeight: "bold", padding: "0 4px",
        }}>
          {unreadCount}
        </span>
      </div>

      {isOpen && (
        <div ref={menuRef} style={{
          position: "absolute", right: 0, marginTop: 12, width: 360,
          background: "var(--color-menu-item-hover)", borderRadius: 16,
          boxShadow: "0 12px 28px rgba(0,0,0,0.2)", overflow: "hidden",
          zIndex: 50, maxHeight: "85vh", display: "flex", flexDirection: "column",
          border: "1px solid rgba(255,255,255,0.1)",
        }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 20, borderBottom: "1px solid rgba(255,255,255,0.1)", background: "rgba(0,0,0,0.1)" }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: "#fff" }}>Notifications</span>
            <button onClick={markAllAsRead} style={{ fontSize: 13, fontWeight: 600, color: "#fff", background: "var(--color-accent-coral)", padding: "8px 16px", borderRadius: 24, border: "none", cursor: "pointer" }}>
              Mark all as read
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", padding: 8, gap: 8, background: "rgba(0,0,0,0.05)" }}>
            {["unread", "read"].map((tab) => (
              <div key={tab} onClick={() => setActiveTab(tab)} style={{
                flex: 1, padding: 12, fontSize: 14, fontWeight: 600, color: "#fff",
                textAlign: "center", background: activeTab === tab ? "var(--color-accent-coral)" : "transparent",
                borderRadius: 12, cursor: "pointer",
              }}>
                {tab === "unread" ? `Unread (${notifications.unread.length})` : `Read (${notifications.read.length})`}
              </div>
            ))}
          </div>

          {/* List */}
          <div style={{ overflowY: "auto", padding: 8 }}>
            {isLoading ? (
              <div style={{ padding: 32, textAlign: "center", color: "rgba(255,255,255,0.7)" }}>Loading...</div>
            ) : list.length > 0 ? (
              list.map((n) => (
                <div key={n.id} onClick={() => !n.is_read && markAsRead(n.id)} style={{
                  padding: 16, marginBottom: 8, borderRadius: 12, cursor: "pointer",
                  background: "rgba(255,255,255,0.08)", transition: "all 0.2s",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 15, fontWeight: 600, color: "#fff" }}>{n.title}</span>
                      {!n.is_read && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#FF6B8B", display: "inline-block" }} />}
                    </div>
                    <span style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>{formatTime(n.created_at)}</span>
                  </div>
                  <div style={{ fontSize: 14, color: "rgba(255,255,255,0.9)", lineHeight: 1.5 }}>{n.body}</div>
                </div>
              ))
            ) : (
              <div style={{ padding: 32, textAlign: "center", color: "rgba(255,255,255,0.7)", fontSize: 15 }}>
                No {activeTab} notifications
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
